import i18n from '@/i18n';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import { useDataStore } from '@/stores/dataStore';
import { speakBlockedIn } from '@/lib/voiceRights';
import { useAuthStore } from '@/stores/authStore';
import { getSocket } from '@/lib/socket';
import { useVoiceMediaSnapshot, voiceMedia } from '@/lib/voiceMedia';
import { useVoiceLocalAudio } from '@/lib/voiceLocalAudio';
import type { Channel, GuildMemberRow, GuildRole, VoiceUserState } from '@/types/api';
import {
  computeMyPermissions,
  computeMyTopRolePosition,
  moderationCapabilities,
} from '@/lib/voiceModeration';
import {
  capacityLabel,
  hasLiveAudioTrack,
  hasLiveVideoTrack,
  screenAudioUserIds,
  streamsByTag,
} from '@/components/contentPane/voiceRoomView';
import {
  toggleSelfMute,
  toggleSelfDeaf,
  toggleSelfScreen,
  leaveVoice,
  joinVoice,
} from '@/lib/voiceCommands';
import { humanizeVoiceMediaError, humanizeVoiceModerationError } from '@/components/contentPane/voiceErrors';
import { askBroadcastConsent } from '@/components/BroadcastConsentModal';

/** Stable empty-array reference for Zustand selectors that may have no entries. */
export const EMPTY_VOICE_LIST: VoiceUserState[] = [];

export interface VoiceRoomProps {
  channelId: string;
  members: GuildMemberRow[];
  roles: GuildRole[];
  ownerId: string;
  voiceChannels: Channel[];
  userLimit: number | null;
}

export type VoiceModerationAction =
  | 'disconnect'
  | 'serverMute'
  | 'serverUnmute'
  | 'serverDeaf'
  | 'serverUndeaf'
  | 'move';

/**
 * Controller hook for VoiceChannelStub. Owns every non-render concern of the
 * voice room — store selectors, derived permission/stream data, the
 * join/error/moderation-menu state, the participant-sync effect, and the
 * mic/deaf/screen/moderation handlers — so the component is left as pure
 * presentation. The behaviour is intentionally identical to the inline version
 * that lived in the component; this is a structural extraction only.
 */
export function useVoiceRoom(props: VoiceRoomProps) {
  // Selector must NOT return a fresh `[]` fallback — Zustand sees a new
  // reference each render and tail-spins. Use undefined and fan it out below.
  const room = useDataStore((s) => s.voiceByChannel[props.channelId]);
  const participants = room ?? EMPTY_VOICE_LIST;
  const currentGuildId = useDataStore((s) => s.currentGuildId);
  // Re-render the channel master-volume control when it changes.
  useVoiceLocalAudio();
  const me = useAuthStore((s) => s.user);
  const myVoiceChannelId = useDataStore((s) => s.myVoiceChannelId);
  const here = myVoiceChannelId === props.channelId;
  const myVoiceState = useDataStore((s) =>
    me && myVoiceChannelId
      ? s.voiceByChannel[myVoiceChannelId]?.find((v) => v.userId === me.id) ?? null
      : null,
  );
  const membersById = useMemo(
    () => new Map(props.members.map((member) => [member.userId, member])),
    [props.members],
  );
  const rolesById = useMemo(
    () => new Map(props.roles.map((role) => [role.id, role])),
    [props.roles],
  );
  const myMember = me ? membersById.get(me.id) ?? null : null;
  const myPermissions = useMemo(
    () => computeMyPermissions(me?.id, props.ownerId, myMember, rolesById),
    [me?.id, myMember, props.ownerId, rolesById],
  );
  const myTopRolePosition = useMemo(
    () => computeMyTopRolePosition(me?.id, props.ownerId, myMember, rolesById),
    [me?.id, myMember, props.ownerId, rolesById],
  );
  // Resolved moderator context — query with moderationCapabilities.
  const mod = useMemo(
    () => ({
      meId: me?.id,
      ownerId: props.ownerId,
      membersById,
      rolesById,
      myPermissions,
      myTopRolePosition,
    }),
    [me?.id, props.ownerId, membersById, rolesById, myPermissions, myTopRolePosition],
  );
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  // Right-click moderation menu target (the tile's hover chips moved here).
  const [modMenu, setModMenu] = useState<{ p: VoiceUserState; x: number; y: number } | null>(null);
  const voiceMediaState = useVoiceMediaSnapshot();
  // Live per-user speaking signal (true while RMS amplitude of their mic
  // stream is above a hysteresis-stabilised threshold).
  const selfMuted = myVoiceState?.selfMute ?? false;
  const selfDeafened = myVoiceState?.selfDeaf ?? false;
  // The room withholds SPEAK: the server holds the mic — show it as off.
  const speakBlocked = useDataStore(speakBlockedIn);
  const deafened = selfDeafened || (myVoiceState?.serverDeaf ?? false);
  // Sound off stops transmitting too, so the mic button shows off while
  // deafened; pressing it brings both back (voiceCommands.toggleSelfMute).
  const muted = selfMuted || (myVoiceState?.serverMute ?? false) || speakBlocked || deafened;
  const screenOn = myVoiceState?.screenOn ?? false;
  const remoteCameraStreamsByUserId = useMemo(
    () => streamsByTag(voiceMediaState.remoteStreams, 'camera'),
    [voiceMediaState.remoteStreams],
  );
  const remoteScreenStreamsByUserId = useMemo(
    () => streamsByTag(voiceMediaState.remoteStreams, 'screen'),
    [voiceMediaState.remoteStreams],
  );
  // Who is sharing sound along with their screen — a silent share and one with
  // system audio are otherwise indistinguishable on a tile.
  const screenAudioBy = useMemo(
    () => screenAudioUserIds(voiceMediaState.remoteStreams),
    [voiceMediaState.remoteStreams],
  );
  const localCameraStream = voiceMediaState.localCameraStream;
  const localScreenStream = voiceMediaState.localScreenStream;
  const localCameraOn = hasLiveVideoTrack(localCameraStream);
  const localScreenOn = hasLiveVideoTrack(localScreenStream);
  const capacityText = capacityLabel(participants.length, props.userLimit);

  useEffect(() => {
    setVoiceError(null);
    setJoining(false);
  }, [props.channelId]);

  useEffect(() => {
    if (here) {
      setVoiceError(null);
      setJoining(false);
    }
  }, [here]);

  // Sync the SFU participant set ONLY when the *userId set* changes.
  // Previously this also re-fired on every mute/unmute push because
  // `participants` is a fresh array reference each time (see dataStore's
  // applyVoiceUserState). Re-running syncParticipants tears down + rebuilds
  // peer connections, which was the biggest perf cliff in the voice room.
  const participantsKey = useMemo(
    () => participants.map((p) => p.userId).sort().join('|'),
    [participants],
  );
  // Ref so we read the *current* array contents inside the effect without
  // making the array itself an effect dependency.
  const participantsRef = useRef(participants);
  participantsRef.current = participants;
  useEffect(() => {
    if (!here || !me) return;
    void voiceMedia
      .syncParticipants(props.channelId, me.id, participantsRef.current)
      .catch((err) => {
        setVoiceError(
          humanizeVoiceMediaError(err instanceof Error ? err.message : 'microphone_access_failed'),
        );
      });
    // `participantsRef.current` is read inside but intentionally not a dep — refs
    // are not reactive. `participantsKey` (a stable string of sorted userIds) is
    // the reactive signal; the ref gives us the current array at run time without
    // making the full array (a new reference on every mute event) a dep.
    // `voiceMedia` is a module-level singleton; `setVoiceError` is a stable setter.
  }, [here, me, props.channelId, participantsKey, setVoiceError]);

  function join() {
    if (joining) return;
    if (!me) return;
    setVoiceError(null);
    joinVoice(props.channelId, {
      onPending: setJoining,
      onError: setVoiceError,
      onConsentRequired: askBroadcastConsent,
    });
  }
  function leave() {
    leaveVoice();
    setVoiceError(null);
  }
  function toggleMute() {
    toggleSelfMute({
      inVoice: here,
      selfMuted,
      selfDeafened,
      serverMute: myVoiceState?.serverMute ?? false,
      serverDeaf: myVoiceState?.serverDeaf ?? false,
    });
  }
  function toggleDeaf() {
    toggleSelfDeaf({
      inVoice: here,
      selfMuted,
      selfDeafened,
      serverMute: myVoiceState?.serverMute ?? false,
      serverDeaf: myVoiceState?.serverDeaf ?? false,
    });
  }
  // Camera toggle removed from UI — voiceMedia.setVideoEnabled stays in the lib
  // for future re-enable; the UI button no longer references it.
  async function toggleScreen() {
    await toggleSelfScreen(
      { inVoice: here, screenOn },
      { onError: (msg) => setVoiceError(humanizeVoiceMediaError(msg)) },
    );
  }
  function moderateVoice(
    action: VoiceModerationAction,
    targetUserId: string,
    targetChannelId?: string,
  ) {
    const socket = getSocket();
    socket.emit(
      'voice:moderate',
      { action, targetUserId, ...(targetChannelId ? { targetChannelId } : {}) },
      (result?: { ok?: boolean; error?: string }) => {
        if (!result?.ok) setVoiceError(humanizeVoiceModerationError(result?.error));
      },
    );
  }
  // Moderation capabilities for one participant — drives both whether the
  // right-click menu opens and which items it shows.
  function moderationFor(p: VoiceUserState) {
    return moderationCapabilities(mod, p, props.voiceChannels, props.channelId);
  }
  // Re-request the mic after a denial; clears the error + toasts on success.
  async function retryMic() {
    const ok = await voiceMedia.retryMicrophone();
    if (ok) {
      setVoiceError(null);
      toast.success(i18n.t('lib2.voice.micConnected'), { duration: 3000 });
    }
  }

  return {
    participants,
    me,
    here,
    currentGuildId,
    capacityText,
    voiceError: voiceMediaState.error ? humanizeVoiceMediaError(voiceMediaState.error) : voiceError,
    joining,
    muted,
    deafened,
    screenOn,
    localScreenOn,
    localCameraOn,
    localScreenStream,
    localCameraStream,
    remoteScreenStreamsByUserId,
    remoteCameraStreamsByUserId,
    screenAudioBy,
    // My own share: the picker may have failed to attach system audio, and I
    // can't hear my own PC through it, so the tile is the only place it shows.
    localScreenAudioOn: hasLiveAudioTrack(localScreenStream),
    membersById,
    modMenu,
    setModMenu,
    micDenied: voiceMediaState.micDenied,
    join,
    leave,
    toggleMute,
    toggleDeaf,
    toggleScreen,
    moderateVoice,
    moderationFor,
    retryMic,
  };
}
